import { useState, useEffect } from "react";

const fontLink = document.createElement("link");
fontLink.rel = "stylesheet";
fontLink.href = "https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600;700;800&family=DM+Mono:wght@400;500;600&display=swap";
document.head.appendChild(fontLink);

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt$ = n => "$" + Math.round(n).toLocaleString();
const STAGES = [
  { id:"new",       label:"New Lead",              color:"#C9A96E" },
  { id:"attempted", label:"Attempted Contact",     color:"#C9935A" },
  { id:"talking",   label:"Connected / Talking",   color:"#5A8A60" },
  { id:"offer",     label:"Offer Made",            color:"#9B7ED4" },
  { id:"contract",  label:"Under Contract",        color:"#B87AA8" },
  { id:"dead",      label:"Dead / Not Interested", color:"#45454A" },
];

// ─── Mini bar ─────────────────────────────────────────────────────────────────
const Bar = ({ value, max, color }) => (
  <div style={{ flex:1, height:8, background:"#1C1C1F", borderRadius:4, overflow:"hidden" }}>
    <div style={{ width:`${max>0?(value/max)*100:0}%`, height:"100%", background:color, borderRadius:4, transition:"width .6s" }} />
  </div>
);

// ─── Funnel bar ───────────────────────────────────────────────────────────────
const FunnelBar = ({ label, count, total, color }) => {
  const pct = total > 0 ? Math.round((count/total)*100) : 0;
  return (
    <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:10 }}>
      <div style={{ width:140, fontSize:12, color:"#8A8885", flexShrink:0 }}>{label}</div>
      <div style={{ flex:1, height:26, background:"#111113", borderRadius:6, overflow:"hidden" }}>
        <div style={{ width:`${pct}%`, height:"100%", background:color+"33", borderLeft:`3px solid ${color}`, display:"flex", alignItems:"center", paddingLeft:10, transition:"width .7s" }}>
          <span style={{ fontSize:11, fontWeight:700, color, fontFamily:"'DM Mono',monospace" }}>{count}</span>
        </div>
      </div>
      <div style={{ width:36, fontSize:11, color:"#45454A", fontFamily:"'DM Mono',monospace", textAlign:"right", flexShrink:0 }}>{pct}%</div>
    </div>
  );
};

// ─── Score histogram ──────────────────────────────────────────────────────────
const ScoreHistogram = ({ leads }) => {
  const buckets = Array(10).fill(0);
  leads.forEach(l => { const b = Math.min(9, Math.floor((l.score||0)/10)); buckets[b]++; });
  const max = Math.max(...buckets, 1);
  const colors = ["#45454A","#45454A","#45454A","#45454A","#45454A","#C9935A","#C9935A","#C9935A","#C06149","#C06149"];
  return (
    <div>
      <div style={{ display:"flex", alignItems:"flex-end", gap:4, height:80 }}>
        {buckets.map((count,i)=>(
          <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
            <div style={{ fontSize:10, color:"#45454A", fontFamily:"'DM Mono',monospace" }}>{count||""}</div>
            <div style={{ width:"100%", background:colors[i], borderRadius:"3px 3px 0 0", height:`${Math.max(4,(count/max)*56)}px`, transition:"height .5s" }} />
          </div>
        ))}
      </div>
      <div style={{ display:"flex", gap:4, marginTop:4 }}>
        {["0","10","20","30","40","50","60","70","80","90"].map(l=>(
          <div key={l} style={{ flex:1, fontSize:9, color:"#2A2A2E", textAlign:"center", fontFamily:"'DM Mono',monospace" }}>{l}</div>
        ))}
      </div>
    </div>
  );
};

// ─── Hot Lead Row ─────────────────────────────────────────────────────────────
const HotRow = ({ lead, onClick }) => (
  <div onClick={()=>onClick(lead)} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 0", borderBottom:"1px solid #1C1C1F", cursor:"pointer" }}
    onMouseEnter={e=>e.currentTarget.style.background="#141416"}
    onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
    <div style={{ background:"#C06149", color:"#0F0F11", fontSize:9, fontWeight:800, fontFamily:"'DM Mono',monospace", borderRadius:4, padding:"2px 6px", flexShrink:0 }}>HOT</div>
    <div style={{ flex:1, minWidth:0 }}>
      <div style={{ fontSize:13, fontWeight:700, color:"#E8E6E1", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{lead.name}</div>
      <div style={{ fontSize:10, color:"#45454A", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{lead.address}</div>
    </div>
    <div style={{ fontSize:15, fontWeight:800, color:"#C9A96E", fontFamily:"'DM Mono',monospace", flexShrink:0 }}>{lead.score}</div>
  </div>
);

// ─── Activity Feed ────────────────────────────────────────────────────────────
function buildActivity(leads) {
  const events = [];
  leads.forEach(l => {
    if (l.stage === "contract") events.push({ text:`${l.name} — under contract`, time:"Recent", color:"#B87AA8" });
    if (l.hot)                  events.push({ text:`${l.name} flagged HOT — score ${l.score}`, time:"Recent", color:"#C06149" });
    if (l.arv)                  events.push({ text:`${l.name} — ARV ${fmt$(l.arv.value)} / MAO ${fmt$(l.arv.mao)}`, time:"Recent", color:"#C9A96E" });
    if (l.smsHistory?.length > 1) events.push({ text:`${l.name} — ${l.smsHistory.length} SMS messages`, time:"Recent", color:"#5A8A60" });
  });
  return events.slice(0, 7);
}

// ─── Alert Banner ─────────────────────────────────────────────────────────────
const AlertBanner = ({ leads }) => {
  const stale = leads.filter(l => l.hot && !l.lastContact);
  if (!stale.length) return null;
  return (
    <div style={{ background:"#C9935A14", border:"1px solid #C9935A44", borderRadius:10, padding:"12px 16px", marginBottom:20, display:"flex", alignItems:"center", gap:12 }}>
      <div style={{ fontSize:18, flexShrink:0 }}>⚠</div>
      <div style={{ fontSize:13, color:"#C9935A", flex:1 }}>
        <strong>{stale.length} hot lead{stale.length>1?"s":""}</strong> with no contact yet — {stale.map(l=>l.name).join(", ")}
      </div>
    </div>
  );
};

// ─── Main Dashboard ───────────────────────────────────────────────────────────
export default function Dashboard({ leads, onSelectLead, onGoToCRM }) {
  const total    = leads.length;
  const hot      = leads.filter(l=>l.hot);
  const contract = leads.filter(l=>l.stage==="contract");
  const pipeline = leads.filter(l=>l.stage==="contract"&&l.wholesaleFee).reduce((s,l)=>s+(l.wholesaleFee||0),0);
  const avgScore = total ? Math.round(leads.reduce((s,l)=>s+(l.score||0),0)/total) : 0;
  const contacted= leads.filter(l=>l.smsHistory?.length>0);
  const activity = buildActivity(leads);

  const card = { background:"#0F0F11", border:"1px solid #1C1C1F", borderRadius:10, padding:"16px 18px" };

  return (
    <div style={{ flex:1, overflowY:"auto", padding:24, background:"#0A0A0B", fontFamily:"'DM Sans',sans-serif" }}>

      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
        <div>
          <div style={{ fontSize:9, letterSpacing:3, color:"#2A2A2E", fontFamily:"'DM Mono',monospace" }}>WHOLESALEIQ</div>
          <div style={{ fontFamily:"'Bebas Neue',display", fontSize:28, letterSpacing:3, color:"#E8E6E1", lineHeight:1.1 }}>DASHBOARD</div>
        </div>
        <button onClick={onGoToCRM} style={{ background:"#C9A96E14", border:"1px solid #C9A96E33", borderRadius:8, color:"#C9A96E", padding:"8px 16px", cursor:"pointer", fontSize:12, fontFamily:"'DM Mono',monospace", fontWeight:700 }}>
          → CRM BOARD
        </button>
      </div>

      {/* Alert */}
      <AlertBanner leads={leads} />

      {/* Stat cards */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(5,minmax(0,1fr))", gap:10, marginBottom:20 }}>
        {[
          { label:"TOTAL LEADS",     value:total,             color:"#C4C2BD" },
          { label:"🔥 HOT LEADS",    value:hot.length,        color:"#C06149" },
          { label:"UNDER CONTRACT",  value:contract.length,   color:"#B87AA8" },
          { label:"EST. WHOLESALE FEES", value:fmt$(pipeline),    color:"#5A8A60" },
          { label:"AVG LEAD SCORE",  value:avgScore,          color:"#C9A96E" },
        ].map((s,i)=>(
          <div key={i} style={{ ...card }}>
            <div style={{ fontSize:9, letterSpacing:2, color:"#45454A", fontFamily:"'DM Mono',monospace", marginBottom:8 }}>{s.label}</div>
            <div style={{ fontSize:22, fontWeight:800, color:s.color, fontFamily:"'DM Mono',monospace" }}>{s.value}</div>
          </div>
        ))}
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1.3fr 1fr", gap:16, marginBottom:16 }}>

        {/* Stage breakdown */}
        <div style={card}>
          <div style={{ fontSize:9, letterSpacing:3, color:"#45454A", fontFamily:"'DM Mono',monospace", marginBottom:14 }}>LEADS BY STAGE</div>
          {STAGES.map(s=>{
            const count = leads.filter(l=>l.stage===s.id).length;
            return (
              <div key={s.id} style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
                <div style={{ width:7, height:7, borderRadius:"50%", background:s.color, flexShrink:0 }} />
                <div style={{ fontSize:12, color:"#8A8885", width:140, flexShrink:0 }}>{s.label}</div>
                <Bar value={count} max={total} color={s.color} />
                <div style={{ fontSize:12, fontWeight:700, color:s.color, fontFamily:"'DM Mono',monospace", width:20, textAlign:"right", flexShrink:0 }}>{count}</div>
              </div>
            );
          })}
        </div>

        {/* Hot leads */}
        <div style={card}>
          <div style={{ fontSize:9, letterSpacing:3, color:"#45454A", fontFamily:"'DM Mono',monospace", marginBottom:10 }}>🔥 HOT LEADS</div>
          {hot.length === 0 && (
            <div style={{ fontSize:12, color:"#2A2A2E", padding:"20px 0", textAlign:"center" }}>No hot leads yet — run AI scoring on your leads</div>
          )}
          {hot.sort((a,b)=>b.score-a.score).slice(0,5).map((l,i)=>(
            <HotRow key={l.id} lead={l} onClick={onSelectLead} />
          ))}
          {hot.length > 5 && (
            <div style={{ fontSize:11, color:"#C9A96E", marginTop:8, cursor:"pointer" }} onClick={onGoToCRM}>
              +{hot.length-5} more hot leads →
            </div>
          )}
        </div>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:16 }}>

        {/* Conversion funnel */}
        <div style={card}>
          <div style={{ fontSize:9, letterSpacing:3, color:"#45454A", fontFamily:"'DM Mono',monospace", marginBottom:14 }}>CONVERSION FUNNEL</div>
          <FunnelBar label="Total leads"       count={total}                                                      total={total} color="#C9A96E" />
          <FunnelBar label="Contacted"         count={contacted.length}                                           total={total} color="#5A8A60" />
          <FunnelBar label="Connected/Talking" count={leads.filter(l=>["talking","offer","contract"].includes(l.stage)).length} total={total} color="#9B7ED4" />
          <FunnelBar label="Offer Made"        count={leads.filter(l=>["offer","contract"].includes(l.stage)).length}           total={total} color="#B87AA8" />
          <FunnelBar label="Under Contract"    count={contract.length}                                            total={total} color="#C06149" />
          <div style={{ marginTop:14, paddingTop:12, borderTop:"1px solid #1C1C1F", display:"flex", gap:20 }}>
            <div>
              <div style={{ fontSize:9, color:"#45454A", fontFamily:"'DM Mono',monospace" }}>CONTACT RATE</div>
              <div style={{ fontSize:16, fontWeight:800, color:"#5A8A60", fontFamily:"'DM Mono',monospace" }}>
                {total ? Math.round((contacted.length/total)*100) : 0}%
              </div>
            </div>
            <div>
              <div style={{ fontSize:9, color:"#45454A", fontFamily:"'DM Mono',monospace" }}>CONTRACT RATE</div>
              <div style={{ fontSize:16, fontWeight:800, color:"#B87AA8", fontFamily:"'DM Mono',monospace" }}>
                {total ? Math.round((contract.length/total)*100) : 0}%
              </div>
            </div>
            <div>
              <div style={{ fontSize:9, color:"#45454A", fontFamily:"'DM Mono',monospace" }}>AVG SCORE</div>
              <div style={{ fontSize:16, fontWeight:800, color:"#C9A96E", fontFamily:"'DM Mono',monospace" }}>{avgScore}</div>
            </div>
          </div>
        </div>

        {/* Activity feed */}
        <div style={card}>
          <div style={{ fontSize:9, letterSpacing:3, color:"#45454A", fontFamily:"'DM Mono',monospace", marginBottom:12 }}>RECENT ACTIVITY</div>
          {activity.length === 0 && (
            <div style={{ fontSize:12, color:"#2A2A2E", padding:"20px 0", textAlign:"center" }}>No activity yet</div>
          )}
          {activity.map((a,i)=>(
            <div key={i} style={{ display:"flex", alignItems:"flex-start", gap:10, padding:"9px 0", borderBottom: i<activity.length-1?"1px solid #1C1C1F":"none" }}>
              <div style={{ width:7, height:7, borderRadius:"50%", background:a.color, flexShrink:0, marginTop:4 }} />
              <div style={{ fontSize:12, color:"#8A8885", flex:1, lineHeight:1.45 }}>{a.text}</div>
              <div style={{ fontSize:10, color:"#45454A", flexShrink:0 }}>{a.time}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Score distribution */}
      <div style={card}>
        <div style={{ fontSize:9, letterSpacing:3, color:"#45454A", fontFamily:"'DM Mono',monospace", marginBottom:12 }}>LEAD SCORE DISTRIBUTION</div>
        <div style={{ display:"flex", gap:16, marginBottom:14 }}>
          {[["🔥 Hot (80–100)","#C06149",leads.filter(l=>l.score>=80).length],["Warm (55–79)","#C9935A",leads.filter(l=>l.score>=55&&l.score<80).length],["Cold (0–54)","#45454A",leads.filter(l=>l.score<55).length]].map(([label,color,count])=>(
            <div key={label} style={{ display:"flex", alignItems:"center", gap:6 }}>
              <div style={{ width:10, height:10, borderRadius:2, background:color }} />
              <span style={{ fontSize:11, color:"#6B6B70" }}>{label} — <strong style={{ color:"#C4C2BD" }}>{count}</strong></span>
            </div>
          ))}
        </div>
        <ScoreHistogram leads={leads} />
      </div>

    </div>
  );
}
